// Copyright (c) Ping. All rights reserved.
//
// Call session store — the orchestration brain for web calls. One active call
// at a time plus a map of incoming rings. Drives the state machine
//   idle → outgoing/incoming → connecting → connected → reconnecting → ended
// and ties together the three subsystems:
//   1. Signaling  — call_invite/accept/decline/end AppEvents on the HOST
//      conversation, sent via `conv.send` and received via `client.onMessage`
//      (see signalingFeed.ts + @ping/ui calls/signaling).
//   2. E2EE key   — MLS-derived media key from the host conversation
//      (livekit/mediaKey.ts).
//   3. Media      — LiveKit room (livekit/LiveKitCallClient.ts).
//
// This file is web-only by design (LiveKit + SDK wiring). iOS/desktop mirror
// the same state machine over their native LiveKit SDKs — see docs/ios-parity
// and docs/desktop-parity.

import { create } from 'zustand';

import {
  buildAccept,
  buildDecline,
  buildEnd,
  buildHandoff,
  buildInvite,
  buildRoster,
  bytesToHex,
  type CallMedia,
  type CallSignalEvent,
  type CallSignalSession,
  dismissBannersForConversation,
  encodeEnvelopeJsonBytes,
  type GroupMemberInput,
  hexToBytes,
  type MessagingClientLike,
  newCallIdHex,
  persistSentMessage,
  reduceCallSignal,
  registerCallGroup,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import {
  type BgEffectKind,
  reapplyBackground,
  resetBackground,
  setBackground,
} from '@calls/effects/backgroundController';
import { loadBgSelection, persistBgSelection } from '@calls/effects/presetBackgrounds';
import {
  type CallDataFrame,
  type CallEffectKind,
  decodeCallData,
  type DrawStroke,
  encodeCallData,
} from '@calls/livekit/callData';
import {
  type CallConnState,
  LiveKitCallClient,
  type LiveKitParticipantView,
} from '@calls/livekit/LiveKitCallClient';
import { deriveCallMediaKey, installMediaKeyRotation } from '@calls/livekit/mediaKey';
import { addWebCallGroupMembers, createWebCallGroup } from '@calls/mls/callGroup';
import { isReloadAttemptRecent } from '@calls/reloadGuard';
import { log } from '@core/log/log';
import {
  endCall as endCallApi,
  mintCallToken,
  startRecording as startRecordingApi,
  stopRecording as stopRecordingApi,
} from '@core/network/calls';
import { claimDevicesForGroupMember } from '@core/network/users';
import { purgeConversation } from '@core/persistence/conversations';
import { webCallNotesPersister } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { webConversationsPersister, webMessagesPersister } from '@features/chat/persistenceAdapters';
import { toastStore } from '@features/toasts/toastStore';

// Richer SDK surface than sessionStore's narrow interface (same cast pattern as
// session/ephemeralSession.ts).
interface CallSdkClient {
  getConversation(id: Uint8Array): {
    send(plaintext: Uint8Array): Promise<unknown>;
    meta(): { epoch: number | bigint };
  };
  exportConversationSecret(
    conv: Uint8Array,
    label: string,
    context: Uint8Array,
    length: number,
  ): Promise<Uint8Array>;
  onConversationUpdated(
    handler: (id: Uint8Array, epoch: number, sender?: Uint8Array) => void,
  ): () => void;
  onMessage(handler: (m: unknown) => void): () => void;
}

function getClient(): CallSdkClient | null {
  const s = useSessionStore.getState().status;
  return s.kind === 'authenticated' ? (s.client as unknown as CallSdkClient) : null;
}

export type CallPhase =
  | 'idle'
  | 'outgoing'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'ended';

export interface ActiveCall {
  readonly callId: string;
  /** Host conversation id (hex) — also the media-key derivation group. */
  readonly conversationId: string;
  /** Per-call MLS group id (hex) — the media-key derivation group + token authz group. */
  readonly callGroupId: string;
  readonly media: CallMedia;
  readonly direction: 'outgoing' | 'incoming';
  /** Peer name (1:1) or group name. */
  readonly title: string;
  readonly isGroup: boolean;
  /** Peer avatar (1:1). */
  readonly avatarUri?: string;
  readonly startedAt: number | null;
  /**
   * Accounts currently in the per-call MLS group (excl. self) — the authoritative
   * roster for invite dedupe. LiveKit participants are unreliable here (metadata
   * accountId may be unset), so re-inviting an existing member off that list
   * slipped through and the MLS add failed "Duplicate signature key in proposals
   * and group". Seeded at startCall; extended on a successful invite.
   */
  readonly groupMemberAccountIds?: readonly string[];
}

export interface CallReactionFlashView {
  readonly id: string;
  readonly emoji: string;
  /** Sender display name — drives the initials avatar shown beside the emoji. */
  readonly fromAccount: string;
  readonly at: number;
}

/** One in-flight full-screen effect (confetti / hearts / fireworks). */
export interface CallEffectView {
  readonly id: string;
  readonly effect: CallEffectKind;
  /** Sender display name — shown with an avatar on the full-screen overlay. */
  readonly fromName: string;
  readonly at: number;
}

/** One in-call (Call Thread) chat message — ephemeral, per call. */
export interface CallThreadMessage {
  readonly id: string;
  readonly fromIdentity: string;
  readonly fromName: string;
  readonly text: string;
  readonly at: number;
  /** Whether this device authored it (right-align / own styling). */
  readonly isSelf: boolean;
  /** emoji → count, plus whether self reacted. */
  readonly reactions: Record<string, { count: number; mine: boolean }>;
  /** Set when this is a "raised hand" system entry. */
  readonly raisedHand?: boolean;
}

/** One Call-Notes action item. */
export interface CallNotesItem {
  readonly id: string;
  readonly text: string;
  readonly done: boolean;
  /** Optional assignee display name (Figma "Anna Chen"). */
  readonly assignee?: string;
  /** Optional due date, ISO-ish display string (Figma "03/02/2025"). */
  readonly due?: string;
}
/** Collaborative Call-Notes doc — sectioned meeting notes synced live over the
 *  data channel (LWW by rev) and persisted on call teardown. Matches the
 *  Ping-Desktop "Notes" panel (4079:11387): Discussion Points + Decisions free
 *  text, an Action-Items checklist, and a free-form notes body (`text`). New
 *  fields are optional so older peers (which only know `text`/`items`) round-trip
 *  cleanly under whole-doc LWW. */
export interface CallNotesDoc {
  readonly text: string;
  readonly items: readonly CallNotesItem[];
  /** "Discussion Points" section free text. */
  readonly discussion?: string;
  /** "Decisions" section free text. */
  readonly decisions?: string;
}
export const EMPTY_NOTES: CallNotesDoc = { text: '', items: [], discussion: '', decisions: '' };

/** Which in-call right panel is open. */
export type CallPanel = 'none' | 'thread' | 'notes' | 'people' | 'invite';

interface CallStore {
  phase: CallPhase;
  active: ActiveCall | null;
  participants: LiveKitParticipantView[];
  /**
   * Live in-call roster (ACCOUNT ids) per call_id, learned from
   * `com.ping.call_roster` presence pings — so a member NOT in the call sees a
   * live participant count/avatars on the ongoing call tile as people join and
   * leave. Last-received wins; cleared when the call ends. Distinct from
   * `participants` (LiveKit, only populated for THIS device's active call).
   */
  liveRosterByCallId: Record<string, ReadonlyArray<string>>;
  connState: CallConnState | null;
  /** True when media is E2EE; false on browsers without Encoded Transform. */
  e2eeActive: boolean;
  /** Remote audio blocked by autoplay policy — UI shows "tap to enable sound". */
  audioBlocked: boolean;
  /** LiveKit identities currently speaking (for tile ordering + ring). */
  activeSpeakers: string[];
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenSharing: boolean;
  /** PIP / minimized active-call window. */
  minimized: boolean;
  handRaised: boolean;
  /** identity → raised, for remote participants' tile overlays. */
  raisedHands: Record<string, boolean>;
  /** Self "be right back / away" toggle — persists on your tile until cleared. */
  brb: boolean;
  /** identity → away, for remote participants' tile overlays. */
  awayParticipants: Record<string, boolean>;
  /** identity → locally muted (silenced for THIS user only, not for everyone). */
  mutedParticipants: Record<string, boolean>;
  /** In-flight full-screen effects (confetti / hearts / fireworks). */
  effects: CallEffectView[];
  /** Open right panel (thread / notes / people). */
  panel: CallPanel;
  /** Selected background effect for the local camera. */
  backgroundEffect: BgEffectKind;
  /** When `backgroundEffect === 'image'`, which image: a preset id or 'custom'.
   * Drives the selected-tile highlight in the effects picker. */
  backgroundImageId: string | null;
  /** Annotation/drawing over the call (shared via data channel). */
  drawMode: boolean;
  drawColor: string;
  drawings: DrawStroke[];
  /** Remote in-progress strokes (live, per-frame), keyed by sender `from`. */
  liveStrokes: Record<string, DrawStroke>;
  /** Workspace recording active (LiveKit Egress) — drives the red indicator. */
  isRecording: boolean;
  /** Server has an S3 sink configured, so "Record call" actually works. When
   * false the affordance is hidden (no point hitting a 503). Set from the
   * token-mint response on connect. */
  recordingAvailable: boolean;
  /** Incoming "continue call on this device?" prompt (from your other device). */
  handoffPrompt: { callId: string; callGroupId: string; media: CallMedia } | null;
  /** In-call chat (Call Thread) — ephemeral, lives only for the call. */
  thread: CallThreadMessage[];
  /** Unread Call-Thread messages while the thread panel is closed (badge). */
  threadUnread: number;
  /** Collaborative Call-Notes doc (canonical here so it survives to call_end). */
  notesDoc: CallNotesDoc;
  /** LWW revision for `notesDoc` (monotonic ms). */
  notesRev: number;
  /** Content hash (hex) of the call_invite message — the in-call thread root.
   *  In-call thread messages persist as replies under it so they appear in the
   *  chat-room thread (footer "Call threads"). */
  callThreadRoot: string | null;
  /** Incoming + outgoing signal sessions, keyed by call_id. Drives the banner. */
  rings: ReadonlyMap<string, CallSignalSession>;
  reactions: CallReactionFlashView[];
  error: string | null;

  // --- intent ---
  startCall(args: {
    conversationId: string;
    media: CallMedia;
    title: string;
    isGroup: boolean;
    avatarUri?: string;
    /** Other participant accounts to ring (peers; self excluded). */
    peerAccountIds: readonly string[];
    /** accountId → display name, for the call-group claim + tile labels. */
    peerDisplayNames?: Record<string, string>;
  }): Promise<void>;
  acceptCall(callId: string): Promise<void>;
  /** Join an ongoing call from its in-chat tile (must be a call-group member). */
  joinCall(args: {
    callId: string;
    callGroupId: string;
    media: CallMedia;
    conversationId: string;
    title?: string;
    isGroup?: boolean;
    /** Call tile's content hash → in-call thread root (footer "Call threads"). */
    threadRoot?: string;
  }): Promise<void>;
  declineCall(callId: string, reason?: 'declined' | 'busy' | 'timeout'): Promise<void>;
  /** Decline + post `text` into the call's host conversation (quick reply). */
  declineWithReply(callId: string, text: string): Promise<void>;
  /** Locally dismiss an incoming ring (swipe-away) — does NOT decline the call. */
  dismissRing(callId: string): void;
  /** Send a quick message into the chat + dismiss the ring (does NOT decline). */
  quickReply(callId: string, text: string): Promise<void>;
  endCall(reason?: string): Promise<void>;
  toggleMic(): Promise<void>;
  toggleCamera(): Promise<void>;
  toggleScreenShare(): Promise<void>;
  /** Re-pick the shared source (stop + restart) — the "Change screen" action. */
  changeScreenShare(): Promise<void>;
  toggleHandRaise(): Promise<void>;
  /** Toggle the persistent "be right back / away" state on your tile. */
  toggleBrb(): Promise<void>;
  /** Locally mute / unmute a remote participant (silences them for you only). */
  toggleParticipantMute(identity: string): void;
  sendReaction(emoji: string): Promise<void>;
  /** Broadcast a full-screen celebratory effect to everyone in the call. */
  sendEffect(effect: CallEffectKind): Promise<void>;
  setBackgroundEffect(kind: BgEffectKind, opts?: { imageUrl?: string; imageId?: string }): Promise<void>;
  sendThreadMessage(text: string): Promise<void>;
  reactToThreadMessage(msgId: string, emoji: string): Promise<void>;
  /** Local Call-Notes edit → set canonical doc + throttled data-channel publish. */
  setNotesDoc(doc: CallNotesDoc): void;
  setPanel(panel: CallPanel): void;
  setMinimized(v: boolean): void;
  resumeAudio(): Promise<void>;
  // drawing / annotation
  toggleDrawMode(): void;
  setDrawColor(color: string): void;
  /** Broadcast the in-progress stroke live (per frame) so remotes see it draw. */
  sendDrawLive(id: string, points: number[]): void;
  addStroke(points: number[], id?: string): Promise<void>;
  clearDrawings(): Promise<void>;
  // workspace recording
  toggleRecording(): Promise<void>;
  // cross-device handoff
  handoffCall(): Promise<void>;
  acceptHandoff(): Promise<void>;
  dismissHandoff(): void;
  // mid-call invite
  inviteToCall(members: ReadonlyArray<{ accountId: string; displayName: string }>): Promise<void>;
  // device pickers
  listDevices(kind: MediaDeviceKind): Promise<MediaDeviceInfo[]>;
  switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<void>;

  // --- ingestion ---
  ingestSignal(evt: CallSignalEvent): void;
}

// Module-scoped handles (not part of React state).
let lk: LiveKitCallClient | null = null;
let rotationUnsub: (() => void) | null = null;
// Lazily-cached local device id for sync, per-frame live-draw frames (can't await
// retrieveIdentity each pointermove). Live strokes key on the stroke id, so this
// `from` is only informational; 'me' is fine until the cache populates.
let cachedDeviceId: string | null = null;
let resetting = false;
// Set when this device initiated a handoff; it auto-leaves once another of the
// user's own devices joins the room.
let handoffPending = false;
// Whether a remote peer has ever been present this call. Used for the 1-1
// hangup rule: once the (sole) remote leaves the room, the call ends for us
// too — but only after they actually joined (so it doesn't fire pre-answer).
let hadRemoteEver = false;
// Account ids of EVERY participant seen in the room over the call's lifetime
// (accumulated from LiveKit `onParticipants`, incl. people who already left).
// Emitted on the terminal `call_end` as `participants` so the in-chat call tile
// shows the full roster + per-viewer "missed" for ALL members — bystanders,
// early-leavers, and reloaded devices. Reset per call in `reset()`.
// (calls-participant-roster-spec.md.)
let seenCallAccountIds = new Set<string>();
// Last live-roster set THIS device broadcast (sorted-joined account ids), so a
// `com.ping.call_roster` presence ping fires only when the set actually changes.
// Reset per call. Only the deterministic "reporter" (smallest account id
// currently in the call) broadcasts, so a join/leave yields ONE ping, not N.
let lastBroadcastRosterKey = '';
// Meeting-notes LWW tiebreak. `notesRevFrom` is the device that authored the
// CURRENT `notesRev`; `notesLocalDeviceId` is this device (cached at join). At
// an EQUAL rev (two edits in the same ms) the higher deviceId hex wins
// deterministically, so all participants converge instead of each keeping its
// own copy (C-t13). Reset per call.
let notesRevFrom = '';
let notesLocalDeviceId = '';

// Invite expiry (calls.md §6): an unanswered incoming ring auto-times-out, and
// an unanswered outgoing call gives up. Timers are keyed by call_id so
// accept/decline/end cancels them cleanly.
const RING_EXPIRY_MS = 30_000; // callee ring auto-dismiss
const CALLER_WAIT_MS = 60_000; // caller gives up if no one joins
const ringTimers = new Map<string, ReturnType<typeof setTimeout>>();
let callerTimeout: ReturnType<typeof setTimeout> | null = null;

function clearRingTimer(callId: string): void {
  const t = ringTimers.get(callId);
  if (t) {
    clearTimeout(t);
    ringTimers.delete(callId);
  }
}

function clearCallerTimeout(): void {
  if (callerTimeout) {
    clearTimeout(callerTimeout);
    callerTimeout = null;
  }
}

// Auto-dismiss an unanswered incoming ring after RING_EXPIRY_MS — declines with
// reason 'timeout' so the caller is told and stops ringing too.
function scheduleRingExpiry(get: () => CallStore, callId: string): void {
  clearRingTimer(callId);
  ringTimers.set(
    callId,
    setTimeout(() => {
      ringTimers.delete(callId);
      const r = get().rings.get(callId);
      if (r && r.status === 'ringing' && get().active?.callId !== callId) {
        void get().declineCall(callId, 'timeout');
      }
    }, RING_EXPIRY_MS),
  );
}
// All pending timeouts (reaction/flash expiry, error→reset) so teardown can
// cancel them — prevents state updates after the call ends (leak/race).
const timers = new Set<ReturnType<typeof setTimeout>>();

function later(fn: () => void, ms: number): void {
  const id = setTimeout(() => {
    timers.delete(id);
    fn();
  }, ms);
  timers.add(id);
}

function clearTimers(): void {
  for (const id of timers) clearTimeout(id);
  timers.clear();
}

// Per-call MLS groups must never appear in the chat list. The callee's
// join-via-Welcome stores the group with no name, so we register the
// call_group_id (blocks useIncomingConversations from creating a row) AND hard-
// purge any row that was created before the call_invite arrived.
function suppressCallGroup(callGroupId: string): void {
  if (!callGroupId) return;
  registerCallGroup(callGroupId);
  void purgeConversation(callGroupId).catch(() => undefined);
  // Retroactively dismiss any "New chat" banner that appeared in the race
  // window between Welcome arrival (~10ms) and call_invite arrival (~100-500ms).
  dismissBannersForConversation(callGroupId);
}

// Write a local-echo chat row for a call envelope the THIS device sent, so the
// caller also sees the call tile (ongoing/ended) in the conversation history.
async function persistCallEcho(
  conversationIdHex: string,
  envelopeBytes: Uint8Array,
  sendResult: unknown,
  accountId: string,
): Promise<void> {
  try {
    const r = sendResult as {
      content_hash?: Uint8Array;
      epoch?: number | bigint;
      hlc?: { wall_ms?: number | bigint };
    } | null;
    const contentHash =
      r?.content_hash instanceof Uint8Array ? bytesToHex(r.content_hash) : `local-call-${Date.now()}`;
    const epoch = typeof r?.epoch === 'bigint' ? Number(r.epoch) : (r?.epoch ?? 0);
    const sentAt =
      typeof r?.hlc?.wall_ms === 'bigint'
        ? Number(r.hlc.wall_ms)
        : typeof r?.hlc?.wall_ms === 'number'
          ? r.hlc.wall_ms
          : Date.now();
    await persistSentMessage({
      messages: webMessagesPersister,
      conversations: webConversationsPersister,
      record: {
        id: contentHash,
        conversationId: conversationIdHex,
        epoch,
        serverSeq: sentAt,
        contentHash,
        senderAccountId: accountId,
        body: new TextDecoder().decode(envelopeBytes),
        sentAt,
        deliveryState: 'sent',
      },
    });
  } catch (err) {
    log.warn({ event: 'call.echo.failed', err: String(err) });
  }
}

// Post a plain text message into a ring's host conversation (quick replies).
async function sendChatText(get: () => CallStore, callId: string, text: string): Promise<void> {
  const ring = get().rings.get(callId);
  const client = getClient();
  const identity = await retrieveIdentity();
  const trimmed = text.trim();
  if (!ring || !client || trimmed.length === 0) return;
  // The text envelope payload key is `body` (what the receiver + persist layer
  // read via payload.body) — NOT `text`. With the wrong key the message arrived
  // empty / didn't render, so quick-reply "didn't send".
  const envBytes = encodeEnvelopeJsonBytes({ kind: 'com.ping.text', payload: { body: trimmed } });
  try {
    const res = await client.getConversation(hexToBytes(ring.conversationId)).send(envBytes);
    // Local-echo so the REPLIER sees their own message in the chat — self-sent
    // MLS messages are not re-received, so without this the sender never sees it.
    if (identity) void persistCallEcho(ring.conversationId, envBytes, res, identity.accountId);
  } catch (err) {
    log.warn({ event: 'call.quickreply.send.failed', err: String(err) });
  }
}

// Notes are canonical in the STORE (not the panel) so they survive to call_end
// even if the panel was never opened, and remote frames received while the panel
// is closed are still captured + persisted. publishNotesFrame broadcasts a local
// edit over the data channel; ingestNotesFrame folds a remote edit (LWW by rev).
function normalizeNotes(remote: unknown): CallNotesDoc {
  if (!remote || typeof remote !== 'object') return EMPTY_NOTES;
  const r = remote as Partial<CallNotesDoc>;
  return {
    text: typeof r.text === 'string' ? r.text : '',
    discussion: typeof r.discussion === 'string' ? r.discussion : '',
    decisions: typeof r.decisions === 'string' ? r.decisions : '',
    items: Array.isArray(r.items)
      ? r.items.map((it) => {
          const item = it as CallNotesItem;
          const out: CallNotesItem = {
            id: String(item.id ?? Date.now()),
            text: String(item.text ?? ''),
            done: Boolean(item.done),
          };
          return typeof item.assignee === 'string' || typeof item.due === 'string'
            ? { ...out, ...(item.assignee ? { assignee: item.assignee } : {}), ...(item.due ? { due: item.due } : {}) }
            : out;
        })
      : [],
  };
}
function ingestNotesFrame(rev: number, doc: unknown, from: string): void {
  const localRev = useCallStore.getState().notesRev;
  // Accept a strictly-newer rev, OR an equal rev from a higher deviceId (a
  // deterministic tiebreak so concurrent same-ms edits converge, not diverge).
  const wins = rev > localRev || (rev === localRev && from > notesRevFrom);
  if (!wins) return;
  notesRevFrom = from;
  useCallStore.setState({ notesDoc: normalizeNotes(doc), notesRev: rev });
}
let notesPublishTimer: ReturnType<typeof setTimeout> | null = null;
export async function publishNotesFrame(rev: number, doc: unknown): Promise<void> {
  const identity = await retrieveIdentity();
  if (!lk || !identity) return;
  void lk.publishData(encodeCallData({ t: 'notes', rev, doc, from: identity.deviceId }));
}
// Persist the current Call-Notes doc for `callId` (best-effort). Called on call
// teardown so the in-chat call tile can preview + download it later.
function persistCallNotes(callId: string, conversationId: string, title: string): void {
  const { notesDoc } = useCallStore.getState();
  if (notesDoc.text.trim().length === 0 && notesDoc.items.length === 0) return;
  void webCallNotesPersister.put({
    id: callId,
    conversationId,
    title,
    text: notesDoc.text,
    items: notesDoc.items.map((it) => ({ id: it.id, text: it.text, done: it.done })),
    updatedAt: Date.now(),
  });
}

function reset(set: (p: Partial<CallStore>) => void): void {
  // Idempotent: many paths can race to tear down (hangup + disconnect + error).
  if (resetting) return;
  resetting = true;
  // Save the Call-Notes doc for this call (best-effort) BEFORE we clear `active`,
  // so the in-chat call tile can preview + download it later. Runs on every
  // participant's teardown path, so both sides keep their copy.
  const teardownActive = useCallStore.getState().active;
  if (teardownActive) {
    persistCallNotes(teardownActive.callId, teardownActive.conversationId, teardownActive.title);
  }
  if (notesPublishTimer) {
    clearTimeout(notesPublishTimer);
    notesPublishTimer = null;
  }
  clearTimers();
  clearCallerTimeout();
  // Note: ring timers are NOT cleared here — incoming rings can outlive an
  // active call's teardown (e.g. a second caller). They self-cancel on
  // accept/decline/end/expiry.
  if (rotationUnsub) {
    rotationUnsub();
    rotationUnsub = null;
  }
  // Release the background processor BEFORE the room disconnects the camera
  // track, so stopProcessor() can tear down the MediaPipe graph cleanly.
  resetBackground();
  if (lk) {
    void lk.disconnect();
    lk = null;
  }
  set({
    phase: 'idle',
    active: null,
    participants: [],
    connState: null,
    e2eeActive: false,
    audioBlocked: false,
    activeSpeakers: [],
    micEnabled: true,
    cameraEnabled: false,
    screenSharing: false,
    minimized: false,
    handRaised: false,
    raisedHands: {},
    brb: false,
    awayParticipants: {},
    mutedParticipants: {},
    effects: [],
    panel: 'none',
    backgroundEffect: 'none',
    backgroundImageId: null,
    drawMode: false,
    drawColor: '#FF5353',
    drawings: [],
    liveStrokes: {},
    isRecording: false,
    recordingAvailable: false,
    handoffPrompt: null,
    thread: [],
    threadUnread: 0,
    notesDoc: EMPTY_NOTES,
    notesRev: 0,
    callThreadRoot: null,
    reactions: [],
    error: null,
  });
  handoffPending = false;
  hadRemoteEver = false;
  seenCallAccountIds = new Set<string>();
  lastBroadcastRosterKey = '';
  notesRevFrom = '';
  resetting = false;
}

export const useCallStore = create<CallStore>((set, get) => ({
  phase: 'idle',
  active: null,
  participants: [],
  liveRosterByCallId: {},
  connState: null,
  e2eeActive: false,
  audioBlocked: false,
  activeSpeakers: [],
  micEnabled: true,
  cameraEnabled: false,
  screenSharing: false,
  minimized: false,
  handRaised: false,
  raisedHands: {},
  brb: false,
  awayParticipants: {},
  mutedParticipants: {},
  effects: [],
  panel: 'none',
  backgroundEffect: 'none',
  backgroundImageId: null,
  drawMode: false,
  drawColor: '#FF5353',
  drawings: [],
  liveStrokes: {},
  isRecording: false,
  recordingAvailable: false,
  handoffPrompt: null,
  thread: [],
  threadUnread: 0,
  notesDoc: EMPTY_NOTES,
  notesRev: 0,
  callThreadRoot: null,
  rings: new Map<string, CallSignalSession>(),
  reactions: [],
  error: null,

  async startCall(args) {
    const { conversationId, media, title, isGroup, peerAccountIds } = args;
    if (get().phase !== 'idle') {
      log.warn({ event: 'call.start.busy' });
      return;
    }
    const client = getClient();
    const identity = await retrieveIdentity();
    if (!client || !identity) {
      set({ error: 'Not signed in', phase: 'ended' });
      return;
    }
    if (peerAccountIds.length === 0) {
      set({ error: 'No one to call', phase: 'ended' });
      later(() => reset(set), 1200);
      return;
    }
    const callId = newCallIdHex();
    set({
      phase: 'outgoing',
      active: {
        callId,
        conversationId,
        callGroupId: '', // filled once the call group is created
        media,
        direction: 'outgoing',
        title,
        isGroup,
        ...(args.avatarUri !== undefined ? { avatarUri: args.avatarUri } : {}),
        startedAt: null,
        groupMemberAccountIds: peerAccountIds,
      },
      micEnabled: true,
      cameraEnabled: media === 'video',
      minimized: false,
      error: null,
    });

    // Seed the call roster from app data so the persisted `call_end.participants`
    // is reliable even when LiveKit participant metadata (account_id) arrives
    // late or not at all — fixes "ended 1:1 call loses all participant cards".
    // Self always. The PEER is seeded ONLY for 1:1: a connected 1:1 means the
    // peer joined. For GROUPS we do NOT seed invitees (a member who declines /
    // never joins must not appear in the roster — that would also defeat the
    // per-viewer "missed" tile); `onParticipants` records actual group joiners.
    seenCallAccountIds.add(identity.accountId);
    if (!isGroup) {
      for (const a of peerAccountIds) {
        if (a.length > 0) seenCallAccountIds.add(a);
      }
    }

    // 1. Create the per-call MLS group (caller + all callee devices) — one
    //    batched addMembers Commit + Welcome fan-out. The media key derives
    //    from THIS group (per-call PCS). calls.md §3.2.
    let callGroupId: string;
    try {
      const members: GroupMemberInput[] = peerAccountIds.map((accountId) => ({
        accountId,
        displayName: args.peerDisplayNames?.[accountId] ?? accountId.slice(0, 6),
      }));
      const group = await createWebCallGroup({
        client: client as unknown as MessagingClientLike,
        members,
        callId,
      });
      callGroupId = group.callGroupIdHex;
      suppressCallGroup(callGroupId);
    } catch (err) {
      log.error({ event: 'call.group.create.failed', err: String(err) });
      set({ error: 'Could not start the call', phase: 'ended' });
      later(() => reset(set), 1500);
      return;
    }
    const cur = get().active;
    if (cur) set({ active: { ...cur, callGroupId } });

    // 2. Post the invite on the HOST conversation — every member device rings
    //    (multi-device). Carries call_group_id so callees know which group.
    const inviteBytes = buildInvite({
      call_id: callId,
      call_group_id: callGroupId,
      media,
      initiator_device: identity.deviceId,
      actorDisplayName: identity.displayName,
      group: isGroup,
    });
    try {
      const res = await client.getConversation(hexToBytes(conversationId)).send(inviteBytes);
      // Local-echo so the caller sees the "Call ongoing" tile in chat history.
      void persistCallEcho(conversationId, inviteBytes, res, identity.accountId);
      // In-call thread root = the call_id (NOT the invite's content hash). The
      // call_id is in the invite body, so it is byte-identical for the caller's
      // local echo, every callee, and bystanders — guaranteeing ONE shared thread
      // per call. (A content-hash root diverges between a local echo and the
      // received copy → two threads.) The gateway resolves the invite as the
      // thread's root row by matching this call_id.
      set({ callThreadRoot: callId });
    } catch (err) {
      log.error({ event: 'call.invite.send.failed', err: String(err) });
      reset(set);
      return;
    }

    // 3. Caller joins the room immediately and waits for the callee(s).
    await joinRoom(set, { callId, callGroupId, media });
  },

  async acceptCall(callId) {
    const ring = get().rings.get(callId);
    const client = getClient();
    const identity = await retrieveIdentity();
    if (!ring || !client || !identity) return;
    clearRingTimer(callId);
    if (get().phase !== 'idle') {
      // Busy on another call — decline this one.
      await get().declineCall(callId, 'busy');
      return;
    }
    const callGroupId = ring.callGroupId;
    suppressCallGroup(callGroupId);
    set({
      phase: 'connecting',
      active: {
        callId,
        conversationId: ring.conversationId,
        callGroupId,
        media: ring.media,
        direction: 'incoming',
        title: ring.actorDisplayName ?? 'Call',
        isGroup: ring.group === true,
        startedAt: null,
      },
      micEnabled: true,
      cameraEnabled: ring.media === 'video',
      minimized: false,
      // Thread root = the shared call_id (identical on every device) so caller +
      // callee + bystanders all read/write ONE thread.
      callThreadRoot: callId,
    });
    dropRing(set, get, callId);

    try {
      await client.getConversation(hexToBytes(ring.conversationId)).send(
        buildAccept({
          call_id: callId,
          call_group_id: callGroupId,
          accepting_device: identity.deviceId,
        }),
      );
    } catch (err) {
      log.warn({ event: 'call.accept.send.failed', err: String(err) });
    }
    await joinRoom(set, { callId, callGroupId, media: ring.media });
  },

  async joinCall(args) {
    if (get().phase !== 'idle') return; // already in a call
    suppressCallGroup(args.callGroupId);
    clearRingTimer(args.callId);
    set({
      phase: 'connecting',
      active: {
        callId: args.callId,
        conversationId: args.conversationId,
        callGroupId: args.callGroupId,
        media: args.media,
        direction: 'incoming',
        title: args.title ?? 'Call',
        isGroup: args.isGroup === true,
        startedAt: null,
      },
      micEnabled: true,
      cameraEnabled: args.media === 'video',
      minimized: false,
      // Thread root = the shared call_id (identical on every device) → ONE thread.
      callThreadRoot: args.callId,
    });
    await joinRoom(set, { callId: args.callId, callGroupId: args.callGroupId, media: args.media });
  },

  async declineCall(callId, reason = 'declined') {
    const ring = get().rings.get(callId);
    const client = getClient();
    clearRingTimer(callId);
    dropRing(set, get, callId);
    if (!ring || !client) return;
    try {
      await client.getConversation(hexToBytes(ring.conversationId)).send(
        buildDecline({ call_id: callId, reason }),
      );
    } catch (err) {
      log.warn({ event: 'call.decline.send.failed', err: String(err) });
    }
  },

  async declineWithReply(callId, text) {
    await sendChatText(get, callId, text);
    await get().declineCall(callId, 'declined');
  },

  dismissRing(callId) {
    // Local-only: hide the banner on this device, keep the call ringing
    // elsewhere (no decline signal). Used by swipe-to-dismiss + quick reply.
    clearRingTimer(callId);
    dropRing(set, get, callId);
  },

  async quickReply(callId, text) {
    // Approve quick action: send the canned message + dismiss the banner; does
    // NOT decline (the caller keeps ringing; you can still join).
    await sendChatText(get, callId, text);
    get().dismissRing(callId);
  },

  async endCall(reason = 'hangup') {
    const { active } = get();
    const client = getClient();
    const identity = await retrieveIdentity();
    if (active && client) {
      const durationMs = active.startedAt ? Date.now() - active.startedAt : 0;
      // The MLS `call_end` is what flips the chat tile to its terminal state, so
      // it must be posted ONLY when the call is truly over: a 1-1 call (either
      // party leaving ends it) or the LAST participant leaving a group. A
      // non-last group leaver just drops out — others keep talking and the
      // shared tile stays "ongoing". (Backend history still records every
      // leave.) Other remotes still present? → group, not the last to go.
      const otherRemotes = get().participants.filter((p) => !p.isLocal).length;
      const isFinal = !active.isGroup || otherRemotes === 0;
      if (isFinal) {
        // Persist the full roster so every member's tile shows who was in the
        // call + per-viewer "missed" (calls-participant-roster-spec.md).
        const roster = [...seenCallAccountIds].filter((a) => a.length > 0);
        const endBytes = buildEnd({
          call_id: active.callId,
          reason,
          duration_ms: durationMs,
          ...(roster.length > 0 ? { participants: roster } : {}),
        });
        try {
          const res = await client.getConversation(hexToBytes(active.conversationId)).send(endBytes);
          if (identity) void persistCallEcho(active.conversationId, endBytes, res, identity.accountId);
        } catch (err) {
          log.warn({ event: 'call.end.send.failed', err: String(err) });
        }
      }
      // Backend history records every departure (final or not).
      void endCallApi({ callId: active.callId, reason, durationMs });
    }
    reset(set);
  },

  async toggleMic() {
    if (!lk) return;
    const next = !get().micEnabled;
    await lk.setMicEnabled(next);
    set({ micEnabled: next });
  },

  async toggleCamera() {
    if (!lk) return;
    const next = !get().cameraEnabled;
    await lk.setCameraEnabled(next);
    set({ cameraEnabled: next });
    // A fresh camera track loses its processor — re-apply the chosen effect.
    if (next) await reapplyBackground(lk.localCameraTrack());
  },

  async setBackgroundEffect(kind, opts) {
    const imageId = kind === 'image' ? (opts?.imageId ?? get().backgroundImageId) : null;
    // Dedupe: re-selecting the active effect would needlessly tear down and
    // rebuild the MediaPipe processor (a visible flicker), so skip a no-op.
    if (kind === get().backgroundEffect && imageId === get().backgroundImageId) return;
    set({ backgroundEffect: kind, backgroundImageId: imageId });
    // Persist so the next call restores this selection. Presets resolve by id;
    // custom uploads live in the local library (keyed by a `custom:` id) so the
    // restore path can re-resolve the cached data URL.
    persistBgSelection({ kind, imageId });
    if (!lk) return;
    try {
      await setBackground(
        lk.localCameraTrack(),
        kind === 'image' && opts?.imageUrl ? { kind, imageUrl: opts.imageUrl } : { kind },
      );
    } catch (err) {
      log.warn({ event: 'call.background.failed', err: String(err) });
    }
  },

  async toggleScreenShare() {
    if (!lk) return;
    const next = !get().screenSharing;
    try {
      await lk.setScreenShareEnabled(next);
      set({ screenSharing: next });
    } catch (err) {
      // User cancelled the browser picker (NotAllowedError) → not an error.
      log.warn({ event: 'call.screenshare.failed', err: String(err) });
    }
  },

  async changeScreenShare() {
    if (!lk || !get().screenSharing) return;
    try {
      // Stop the current source then re-open the browser picker for a new one.
      await lk.setScreenShareEnabled(false);
      set({ screenSharing: false });
      await lk.setScreenShareEnabled(true);
      set({ screenSharing: true });
    } catch (err) {
      log.warn({ event: 'call.screenshare.change.failed', err: String(err) });
    }
  },

  async toggleHandRaise() {
    if (!lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    const next = !get().handRaised;
    set({ handRaised: next });
    // Realtime via data channel (sub-50ms), not MLS.
    void lk.publishData(
      encodeCallData({ t: 'hand', raised: next, from: identity.deviceId, name: identity.displayName }),
    );
    if (next) appendThread(set, get, makeRaisedHand(identity.deviceId, identity.displayName, true));
  },

  async toggleBrb() {
    if (!lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    const next = !get().brb;
    set({ brb: next });
    void lk.publishData(
      encodeCallData({ t: 'brb', active: next, from: identity.deviceId, name: identity.displayName }),
    );
  },

  toggleParticipantMute(identity) {
    const muted = !get().mutedParticipants[identity];
    set({ mutedParticipants: { ...get().mutedParticipants, [identity]: muted } });
    // Local-only mute: silence (vol 0) or restore (vol 1) on this client.
    lk?.setParticipantVolume(identity, muted ? 0 : 1);
  },

  async sendReaction(emoji) {
    if (!lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    pushReaction(set, get, { emoji, fromAccount: identity.displayName, at: Date.now() });
    void lk.publishData(
      encodeCallData({ t: 'reaction', emoji, from: identity.deviceId, name: identity.displayName }),
    );
  },

  async sendEffect(effect) {
    if (!lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    pushEffect(set, get, { effect, fromName: identity.displayName, at: Date.now() });
    void lk.publishData(
      encodeCallData({ t: 'effect', effect, from: identity.deviceId, name: identity.displayName }),
    );
  },

  async sendThreadMessage(text) {
    const trimmed = text.trim();
    if (!trimmed || !lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    appendThread(set, get, {
      id,
      fromIdentity: identity.deviceId,
      fromName: identity.displayName,
      text: trimmed,
      at: Date.now(),
      isSelf: true,
      reactions: {},
    });
    void lk.publishData(
      encodeCallData({
        t: 'chat',
        id,
        text: trimmed,
        from: identity.deviceId,
        name: identity.displayName,
        at: Date.now(),
      }),
    );
    // DURABLE copy: also post the message as a THREADED reply rooted at the
    // call_invite message, so it persists + appears in the chat-room thread the
    // call tile's "Call threads" button opens (unified with the main thread UI).
    // The live data-channel frame above drives the instant in-call panel; this
    // MLS reply is the persisted record (different surfaces → no duplicate).
    const root = get().callThreadRoot;
    const active = get().active;
    const client = getClient();
    if (root && active && client) {
      const replyBytes = new TextEncoder().encode(
        JSON.stringify({
          kind: 'com.ping.text',
          parent_id: root,
          payload: { body: trimmed, thread: true },
        }),
      );
      try {
        const res = await client.getConversation(hexToBytes(active.conversationId)).send(replyBytes);
        void persistCallEcho(active.conversationId, replyBytes, res, identity.accountId);
      } catch (err) {
        log.warn({ event: 'call.thread.persist.failed', err: String(err) });
      }
    }
  },

  async reactToThreadMessage(msgId, emoji) {
    if (!lk) return;
    const identity = await retrieveIdentity();
    if (!identity) return;
    applyThreadReaction(set, get, msgId, emoji, true);
    void lk.publishData(encodeCallData({ t: 'chat_react', msgId, emoji, from: identity.deviceId }));
  },

  setNotesDoc(doc) {
    // Local edit → bump rev, set canonical doc, throttled data-channel publish.
    const rev = Date.now();
    notesRevFrom = notesLocalDeviceId; // this device authored the current rev
    set({ notesDoc: doc, notesRev: rev });
    if (notesPublishTimer) clearTimeout(notesPublishTimer);
    notesPublishTimer = setTimeout(() => {
      notesPublishTimer = null;
      void publishNotesFrame(rev, doc);
    }, 250);
  },

  setPanel(panel) {
    // Opening the thread clears its unread badge.
    set(panel === 'thread' ? { panel, threadUnread: 0 } : { panel });
  },

  setMinimized(v) {
    set({ minimized: v });
  },

  async resumeAudio() {
    await lk?.startAudioPlayback();
    set({ audioBlocked: false });
  },

  toggleDrawMode() {
    set({ drawMode: !get().drawMode });
  },

  setDrawColor(color) {
    set({ drawColor: color });
  },

  sendDrawLive(id, points) {
    if (!lk || points.length < 2) return;
    if (cachedDeviceId === null) {
      void retrieveIdentity().then((i) => {
        if (i) cachedDeviceId = i.deviceId;
      });
    }
    const from = cachedDeviceId ?? 'me';
    void lk.publishData(
      encodeCallData({ t: 'draw_live', id, color: get().drawColor, points, from }),
    );
  },

  async addStroke(points, id) {
    if (!lk || points.length < 4) return;
    const identity = await retrieveIdentity();
    const from = identity?.deviceId ?? 'me';
    // Reuse the live-preview id (when provided) so remotes replace their live
    // stroke with this committed one instead of showing both.
    const strokeId = id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const stroke: DrawStroke = { id: strokeId, color: get().drawColor, width: 3, points, from };
    // Drop our own live echo + commit.
    const { [strokeId]: _l, ...restLive } = get().liveStrokes;
    void _l;
    set({ drawings: [...get().drawings, stroke].slice(-300), liveStrokes: restLive });
    void lk.publishData(encodeCallData({ t: 'draw', stroke, from }));
  },

  async clearDrawings() {
    const identity = await retrieveIdentity();
    set({ drawings: [], liveStrokes: {} });
    if (lk) void lk.publishData(encodeCallData({ t: 'draw_clear', from: identity?.deviceId ?? 'me' }));
  },

  async toggleRecording() {
    const { active, isRecording, recordingAvailable } = get();
    const identity = await retrieveIdentity();
    if (!active || !lk || !identity) return;
    if (!recordingAvailable) {
      toastStore.show('Recording isn’t enabled on this server.', { type: 'info' });
      return;
    }
    try {
      if (!isRecording) {
        await startRecordingApi({ callId: active.callId, callGroupId: active.callGroupId });
        set({ isRecording: true });
      } else {
        await stopRecordingApi({ callId: active.callId });
        set({ isRecording: false });
      }
      // Broadcast so every participant sees the recording indicator (consent).
      void lk.publishData(
        encodeCallData({
          t: 'recording',
          active: !isRecording,
          from: identity.deviceId,
          name: identity.displayName,
        }),
      );
    } catch (err) {
      log.warn({ event: 'call.recording.failed', err: String(err) });
    }
  },

  async handoffCall() {
    const { active } = get();
    const client = getClient();
    const identity = await retrieveIdentity();
    if (!active || !client || !identity) return;
    const myDeviceHex = b64urlToHex(identity.deviceId);
    const existing = get()
      .participants.map((p) => p.accountId)
      .filter((a): a is string => !!a && a !== identity.accountId);
    try {
      // Add my OTHER devices (exclude the current one) to the call group.
      await addWebCallGroupMembers({
        client: client as unknown as MessagingClientLike,
        callGroupIdHex: active.callGroupId,
        existingPeerAccountIds: existing,
        newMembers: [{ accountId: identity.accountId, displayName: identity.displayName }],
        claimDevicesForAccount: async (acct) => {
          const ds = await claimDevicesForGroupMember(acct);
          return ds.filter((d) => d.deviceIdHex.toLowerCase() !== myDeviceHex);
        },
      });
      // Ring my other devices to take over (they're now in the call group).
      await client.getConversation(hexToBytes(active.conversationId)).send(
        buildHandoff({
          call_id: active.callId,
          call_group_id: active.callGroupId,
          media: active.media,
          account: identity.accountId,
          from_device: identity.deviceId,
        }),
      );
      handoffPending = true; // auto-leave once the other device joins
    } catch (err) {
      log.warn({ event: 'call.handoff.failed', err: String(err) });
    }
  },

  async acceptHandoff() {
    const p = get().handoffPrompt;
    if (!p || get().phase !== 'idle') return;
    set({
      handoffPrompt: null,
      phase: 'connecting',
      active: {
        callId: p.callId,
        conversationId: p.callGroupId, // call_end/signaling rides the call group
        callGroupId: p.callGroupId,
        media: p.media,
        direction: 'incoming',
        title: 'Call',
        isGroup: false,
        startedAt: null,
      },
      micEnabled: true,
      cameraEnabled: p.media === 'video',
      // Same shared call_id thread root as every other join path.
      callThreadRoot: p.callId,
    });
    await joinRoom(set, { callId: p.callId, callGroupId: p.callGroupId, media: p.media });
  },

  dismissHandoff() {
    set({ handoffPrompt: null });
  },

  async inviteToCall(members) {
    const { active } = get();
    const client = getClient();
    const identity = await retrieveIdentity();
    if (!active || !client || !identity || members.length === 0) return;
    try {
      // Authoritative existing roster = the tracked call-group members ∪ live
      // LiveKit participants (the latter alone is unreliable — metadata
      // accountId may be unset, so an already-present member slips through and
      // the MLS add fails "Duplicate signature key in proposals and group").
      const fromParticipants = get()
        .participants.map((p) => p.accountId)
        .filter((a): a is string => !!a);
      const existing = [
        ...new Set([...(active.groupMemberAccountIds ?? []), ...fromParticipants]),
      ].filter((a) => a !== identity.accountId);
      // Drop selections that are already members — nothing to add (and the SDK
      // would reject the duplicate). If none remain, skip the commit + invite.
      const toAdd = members.filter((m) => !existing.includes(m.accountId));
      if (toAdd.length === 0) {
        toastStore.show('Everyone selected is already in the call.', { type: 'info' });
        return;
      }
      await addWebCallGroupMembers({
        client: client as unknown as MessagingClientLike,
        callGroupIdHex: active.callGroupId,
        existingPeerAccountIds: existing,
        newMembers: toAdd.map((m) => ({ accountId: m.accountId, displayName: m.displayName })),
      });
      // Track the now-larger roster so a later invite dedupes correctly.
      const cur = get().active;
      if (cur && cur.callId === active.callId) {
        set({
          active: {
            ...cur,
            groupMemberAccountIds: [...existing, ...toAdd.map((m) => m.accountId)],
          },
        });
      }
      // Post the invite INTO the call group so the new members (now in it via
      // the Welcome) ring. Their global onMessage feed picks it up.
      await client.getConversation(hexToBytes(active.callGroupId)).send(
        buildInvite({
          call_id: active.callId,
          call_group_id: active.callGroupId,
          media: active.media,
          initiator_device: identity.deviceId,
          actorDisplayName: identity.displayName,
        }),
      );
    } catch (err) {
      log.warn({ event: 'call.invite.failed', err: String(err) });
    }
  },

  async listDevices(kind) {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      return all.filter((d) => d.kind === kind);
    } catch {
      return [];
    }
  },

  async switchDevice(kind, deviceId) {
    await lk?.switchDevice(kind, deviceId);
  },

  ingestSignal(evt) {
    const active = get().active;
    // Group-ness of THIS call: the live active call if it's this one, else the
    // ring session (a bystander callee has no `active` yet but does have a ring).
    const isGroupCall =
      active?.callId === evt.call_id
        ? active.isGroup
        : get().rings.get(evt.call_id)?.group === true;

    // Fold into the ring/session projection first — EXCEPT a group decline. A
    // group ring session is one shared per-call_id entry; letting a single
    // member's `decline` transition it to `declined` would hide the still-live
    // call from every other still-ringing invitee (calls.md §3.5/§3.6,
    // calls-drift.md §18). The decliner already dropped its own ring locally in
    // `declineCall`, so the fanned echo must be a no-op here for groups.
    if (!(evt.type === 'decline' && isGroupCall)) {
      set({ rings: reduceCallSignal(get().rings, evt) });
    }

    switch (evt.type) {
      case 'invite': {
        // The call group must never become a chat row on the callee — its
        // Welcome arrives nameless, so register + purge it now.
        suppressCallGroup(evt.call_group_id);
        // Busy auto-decline: already on a call → tell the caller.
        if (active && active.callId !== evt.call_id) {
          void get().declineCall(evt.call_id, 'busy');
          return;
        }
        // Auto-expire the ring if unanswered (invite timeout).
        scheduleRingExpiry(get, evt.call_id);
        return;
      }
      case 'accept':
        suppressCallGroup(evt.call_group_id);
        clearRingTimer(evt.call_id);
        // Caller stays connected; the remote appears in the LiveKit room.
        return;
      case 'decline': {
        // GROUP: one invitee declining must NOT end the call for the caller and
        // must NOT clear the ring on other still-ringing invitees — the call
        // stays live until the last participant leaves. The declining device
        // already cleared its own ring in `declineCall`. (calls.md §3.5/§3.6;
        // calls-drift.md §18 — the decline→terminal fold is scoped to 1:1.)
        if (isGroupCall) return;
        clearRingTimer(evt.call_id);
        if (active && active.direction === 'outgoing' && active.callId === evt.call_id) {
          // Send call_end so the call_invite tile flips from "ongoing" to
          // "missed/declined" for every participant (caller + callee both need
          // it). Without this the tile stays "ongoing" permanently. The
          // decline itself is callee→caller only; call_end is the signal
          // that makes the chat history terminal.
          const endReason = evt.reason === 'timeout' ? 'timeout' : 'declined';
          const endError = evt.reason === 'busy' ? 'Busy' : evt.reason === 'timeout' ? 'No answer' : 'Call declined';
          const client = getClient();
          if (client) {
            const endBytes = buildEnd({ call_id: active.callId, reason: endReason, duration_ms: 0 });
            void client.getConversation(hexToBytes(active.conversationId)).send(endBytes)
              .then((res) =>
                retrieveIdentity().then((id) => {
                  if (id) void persistCallEcho(active.conversationId, endBytes, res, id.accountId);
                }),
              )
              .catch(() => undefined);
            void endCallApi({ callId: active.callId, reason: endReason, durationMs: 0 });
          }
          set({ phase: 'ended', error: endError });
          later(() => reset(set), 1500);
        }
        // Prune the terminal session so `rings` never grows unbounded — a
        // declined call is no longer a banner (CallHost shows only 'ringing').
        dropRing(set, get, evt.call_id);
        return;
      }
      case 'roster': {
        // Live presence ping from an in-call member → update the ongoing-call
        // tile roster for viewers NOT in the call. Last-received wins (join/
        // leave propagate quickly; the tile reads the latest set). Ignore a ping
        // for a call we're actively in — we already have authoritative LiveKit
        // participants for it.
        if (active?.callId === evt.call_id) return;
        set((s) => ({
          liveRosterByCallId: { ...s.liveRosterByCallId, [evt.call_id]: evt.participants },
        }));
        return;
      }
      case 'end': {
        clearRingTimer(evt.call_id);
        // The call is over → drop its live roster (the ended tile now uses the
        // durable `call_end.participants` union instead).
        set((s) => {
          if (s.liveRosterByCallId[evt.call_id] === undefined) return {};
          const next = { ...s.liveRosterByCallId };
          delete next[evt.call_id];
          return { liveRosterByCallId: next };
        });
        if (active && active.callId === evt.call_id) {
          // 1-1: either party ending ends the call for BOTH — tear down now.
          // Group: one participant leaving does NOT end the call; it stays
          // active while ≥1 peer remains (and even for the last person until
          // they leave). Their departure is reflected via LiveKit participant
          // events; the LiveKit `disconnected` path tears us down if the room
          // actually empties for us. So we ignore a remote `end` in a group.
          if (!active.isGroup) reset(set);
        }
        // Prune the ended session (see decline) — keeps the Map bounded.
        dropRing(set, get, evt.call_id);
        return;
      }
      case 'handoff': {
        void handleHandoff(set, get, evt);
        return;
      }
      // reaction / hand_raise now ride the LiveKit data channel (handleData);
      // these MLS variants are accepted but ignored to avoid double-handling.
      case 'reaction':
      case 'hand_raise':
      case 'ringing':
        return;
      default:
        return;
    }
  },
}));

/** base64url (no pad) → lowercase hex. For comparing device ids across forms. */
function b64urlToHex(b64url: string): string {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='));
  let out = '';
  for (let i = 0; i < bin.length; i += 1) out += bin.charCodeAt(i).toString(16).padStart(2, '0');
  return out;
}

/**
 * A handoff arrived. If it's from another of MY devices (same account,
 * different device) and I'm idle, prompt to continue the call here.
 */
async function handleHandoff(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  evt: Extract<CallSignalEvent, { type: 'handoff' }>,
): Promise<void> {
  const id = await retrieveIdentity();
  if (!id) return;
  if (evt.account !== id.accountId) return; // not my account
  if (evt.from_device === id.deviceId) return; // my own echo
  // The handoff added this device to the call group — keep it out of the chat list.
  suppressCallGroup(evt.call_group_id);
  if (get().active || get().handoffPrompt) return; // busy / already prompted
  set({
    handoffPrompt: { callId: evt.call_id, callGroupId: evt.call_group_id, media: evt.media },
  });
}

// --- data-channel dispatch + thread helpers -------------------------------

function handleData(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  frame: CallDataFrame,
  fromIdentity: string | undefined,
): void {
  switch (frame.t) {
    case 'reaction':
      pushReaction(set, get, { emoji: frame.emoji, fromAccount: frame.name, at: Date.now() });
      return;
    case 'hand': {
      const id = fromIdentity ?? frame.from;
      set({ raisedHands: { ...get().raisedHands, [id]: frame.raised } });
      if (frame.raised) appendThread(set, get, makeRaisedHand(id, frame.name, false));
      return;
    }
    case 'brb': {
      const id = fromIdentity ?? frame.from;
      set({ awayParticipants: { ...get().awayParticipants, [id]: frame.active } });
      return;
    }
    case 'effect':
      pushEffect(set, get, { effect: frame.effect, fromName: frame.name, at: Date.now() });
      return;
    case 'chat': {
      if (get().thread.some((m) => m.id === frame.id)) return; // dedupe
      appendThread(set, get, {
        id: frame.id,
        fromIdentity: fromIdentity ?? frame.from,
        fromName: frame.name,
        text: frame.text,
        at: frame.at,
        isSelf: false,
        reactions: {},
      });
      // Badge the Call-Thread control when a remote message lands while the
      // thread panel is closed.
      if (get().panel !== 'thread') set({ threadUnread: get().threadUnread + 1 });
      return;
    }
    case 'chat_react':
      applyThreadReaction(set, get, frame.msgId, frame.emoji, false);
      return;
    case 'draw_live': {
      // Live in-progress stroke from a remote — frames are INCREMENTAL (only the
      // points added since the sender's last frame), so append to any existing
      // buffer for this id. Rendered under its id until the final `draw` frame
      // supersedes it. Keeping frames incremental is what makes a long remote
      // scribble O(n) instead of O(n²) on this receiver.
      const prev = get().liveStrokes[frame.id];
      const points = prev ? [...prev.points, ...frame.points] : frame.points;
      const live: DrawStroke = {
        id: frame.id,
        color: frame.color,
        width: 3,
        points,
        from: frame.from,
      };
      set({ liveStrokes: { ...get().liveStrokes, [frame.id]: live } });
      return;
    }
    case 'draw': {
      // Final stroke supersedes its live preview (same id).
      const { [frame.stroke.id]: _done, ...restLive } = get().liveStrokes;
      void _done;
      if (get().drawings.some((s) => s.id === frame.stroke.id)) {
        set({ liveStrokes: restLive });
        return; // dedupe
      }
      set({ drawings: [...get().drawings, frame.stroke].slice(-300), liveStrokes: restLive });
      return;
    }
    case 'draw_clear':
      set({ drawings: [], liveStrokes: {} });
      return;
    case 'recording':
      set({ isRecording: frame.active });
      return;
    case 'notes':
      // Notes sync handled by the notes store (wired when the panel mounts).
      ingestNotesFrame(frame.rev, frame.doc, frame.from);
      return;
    default:
      return;
  }
}

function makeRaisedHand(fromIdentity: string, fromName: string, isSelf: boolean): CallThreadMessage {
  return {
    id: `hand-${fromIdentity}-${Date.now()}`,
    fromIdentity,
    fromName,
    text: 'Raised hand',
    at: Date.now(),
    isSelf,
    reactions: {},
    raisedHand: true,
  };
}

function appendThread(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  msg: CallThreadMessage,
): void {
  set({ thread: [...get().thread, msg].slice(-200) });
}

function applyThreadReaction(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  msgId: string,
  emoji: string,
  mine: boolean,
): void {
  const thread = get().thread.map((m) => {
    if (m.id !== msgId) return m;
    const prev = m.reactions[emoji] ?? { count: 0, mine: false };
    if (mine && prev.mine) return m; // already reacted
    return {
      ...m,
      reactions: { ...m.reactions, [emoji]: { count: prev.count + 1, mine: prev.mine || mine } },
    };
  });
  set({ thread });
}

// --- helpers --------------------------------------------------------------

async function joinRoom(
  set: (p: Partial<CallStore>) => void,
  args: { callId: string; callGroupId: string; media: CallMedia },
): Promise<void> {
  const get = useCallStore.getState;
  const client = getClient();
  const identity = await retrieveIdentity();
  if (!client || !identity) {
    set({ phase: 'ended', error: 'Not signed in' });
    return;
  }
  set({ phase: 'connecting' });
  try {
    const tok = await mintCallToken({
      callId: args.callId,
      callGroupId: args.callGroupId,
      conversationId: args.callGroupId,
      kind: args.media,
      displayName: identity.displayName,
    });
    // Callee may receive the call_invite before the call group's Welcome is
    // processed — retry the export until the group is locally available.
    const mediaKey = await deriveMediaKeyWithRetry(client, args.callGroupId, args.callId);
    // DIAGNOSTIC: log the MLS epoch and a SHA-256 fingerprint of the raw media
    // key (first 4 bytes of the digest, not the key itself — AGENTS rule 3).
    // Compare epoch + fp against the iOS peer's "call key-fp=" log line to confirm
    // both sides derived from the same MLS state. Mismatch → epoch divergence.
    try {
      const ep = client.getConversation(hexToBytes(args.callGroupId)).meta().epoch;
      log.info({ event: 'call.mediakey.epoch', when: 'connect', callId: args.callId, epoch: Number(ep) });
      const hashBuf = await crypto.subtle.digest('SHA-256', new Uint8Array(mediaKey));
      const fp = Array.from(new Uint8Array(hashBuf))
        .slice(0, 4)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      log.info({ event: 'call.key.fp', fp, callId: args.callId });
    } catch {
      // epoch/fp read is best-effort diagnostics; never block the call on it.
    }

    const selfAccount = identity.accountId;
    notesLocalDeviceId = identity.deviceId; // for the meeting-notes LWW tiebreak
    lk = new LiveKitCallClient({
      onParticipants: (participants) => {
        set({ participants });
        // Accumulate the full call roster (see `seenCallAccountIds`). Self is
        // seeded here too so the emitter always appears in its own `call_end`.
        seenCallAccountIds.add(selfAccount);
        for (const p of participants) {
          if (p.accountId !== undefined && p.accountId.length > 0) {
            seenCallAccountIds.add(p.accountId);
          }
        }
        // Live-presence broadcast (GROUP calls only). Emit a `com.ping.call_roster`
        // ping when the CURRENT in-room set changes so members NOT in the call see
        // a live count/avatars on the ongoing tile as people join/leave. To avoid
        // N devices each broadcasting the same set, only the deterministic
        // "reporter" (smallest account id currently in the room) sends; if the
        // reporter leaves, the next-smallest takes over automatically.
        {
          const a0 = get().active;
          if (a0?.isGroup === true) {
            const current = [
              ...new Set<string>([
                selfAccount,
                ...participants
                  .map((p) => p.accountId)
                  .filter((x): x is string => x !== undefined && x.length > 0),
              ]),
            ].filter((x) => x.length > 0);
            const sorted = [...current].sort();
            const key = sorted.join(',');
            if (sorted[0] === selfAccount && key !== lastBroadcastRosterKey) {
              lastBroadcastRosterKey = key;
              const client = getClient();
              if (client) {
                try {
                  void client
                    .getConversation(hexToBytes(a0.conversationId))
                    .send(buildRoster({ call_id: a0.callId, participants: current }));
                } catch (err) {
                  log.warn({ event: 'call.roster.broadcast.failed', err: String(err) });
                }
              }
            }
          }
        }
        // Keep `screenSharing` in sync with reality — covers the case where the
        // user stops sharing via the browser's native "Stop sharing" bar (which
        // unpublishes the track without going through `toggleScreenShare`).
        const localView = participants.find((p) => p.isLocal);
        if (localView && localView.isScreenSharing !== get().screenSharing) {
          set({ screenSharing: localView.isScreenSharing });
        }
        const hasRemote = participants.some((p) => !p.isLocal);
        // Someone answered → cancel the caller no-answer timeout.
        if (hasRemote) {
          clearCallerTimeout();
          hadRemoteEver = true;
        }
        // Handoff: once another of my own devices joins, this device leaves.
        if (handoffPending && participants.some((p) => !p.isLocal && p.accountId === selfAccount)) {
          handoffPending = false;
          void get().endCall('left');
          return;
        }
        // Peer-left rule — 1:1 ONLY. In a true 1:1, once the sole remote leaves
        // the room (hangup OR crash), the call is over for us too — a 1:1 with
        // only yourself in it isn't a call. `hadRemoteEver` guards against firing
        // before the callee has answered.
        //
        // GROUPS DO NOT auto-end here: being alone in a group call is valid — the
        // remaining member stays connected so anyone can (re)join, and the call
        // only TRULY ends when that last member EXPLICITLY leaves (their
        // `endCall` sees `otherRemotes === 0` → emits the terminal `call_end`).
        // Auto-ending groups when momentarily alone (even a 2-member group) was
        // wrong: it collapsed the call the instant one of two members left,
        // instead of letting the other wait for a rejoin. (calls-drift.md §18.)
        const a = get().active;
        if (a && !a.isGroup && hadRemoteEver && !hasRemote && get().phase === 'connected') {
          void get().endCall('left');
        }
      },
      onActiveSpeakers: (ids) => set({ activeSpeakers: ids }),
      onAudioPlaybackBlocked: () => set({ audioBlocked: true }),
      onData: (payload, from) => {
        const frame = decodeCallData(payload);
        if (frame) handleData(set, get, frame, from);
      },
      onState: (connState) => {
        set({ connState });
        // LiveKit handles transient blips via Reconnecting/Reconnected; the
        // Disconnected event is terminal, so tearing down here is correct — EXCEPT
        // when the user is mid reload/tab-close prompt: the native "Reload site?"
        // dialog suspends the tab and LiveKit can emit a stale `disconnected`; if
        // the user then clicks Cancel we must NOT reset (the call must stay alive).
        // Show reconnecting instead and let LiveKit re-establish once the tab
        // resumes. See `reloadGuard.ts`.
        if (connState === 'reconnecting') set({ phase: 'reconnecting' });
        // Only promote to `connected` while a call is still active — a LiveKit
        // `connected` event can fire during the join-race window after the call
        // was already torn down (active === null); ignoring it there prevents a
        // superseded call from being resurrected on this callback.
        else if (connState === 'connected') {
          if (get().active) set({ phase: 'connected' });
        } else if (connState === 'disconnected' && get().phase !== 'idle') {
          if (isReloadAttemptRecent()) set({ phase: 'reconnecting' });
          else reset(set);
        }
      },
      onError: (err) => log.warn({ event: 'call.livekit.error', err: String(err) }),
    });

    await lk.connect({
      url: tok.url,
      token: tok.token,
      mediaKey,
      audio: true,
      video: args.media === 'video',
    });

    // Join-race guard: a remote `call_end` (or a local teardown) can land while
    // mintCallToken / deriveMediaKeyWithRetry / lk.connect are in flight. If the
    // store no longer considers THIS call active, the awaits above resolved into a
    // torn-down call — disconnect the freshly-connected room instead of resurrecting
    // it. Without this, the `set({ phase: 'connected' })` below would revive a call
    // the rest of the store already ended (iOS has the same guard — calls.md §10).
    if (get().active?.callId !== args.callId || get().phase === 'idle') {
      log.info({ event: 'call.join.superseded', callId: args.callId });
      try {
        await lk.disconnect();
      } catch (err) {
        log.warn({ event: 'call.join.superseded.disconnect.failed', err: String(err) });
      }
      return;
    }

    // Assign rotation unsub immediately (no window between install + assign).
    rotationUnsub = installMediaKeyRotation({
      client,
      keyProvider: lk.keyProvider,
      conversationIdHex: args.callGroupId,
      callIdHex: args.callId,
      onError: (err) => log.warn({ event: 'call.mediakey.rotate.failed', err: String(err) }),
      onEpoch: (epoch) =>
        log.info({ event: 'call.mediakey.epoch', when: 'rotate', callId: args.callId, epoch }),
    });

    set({
      phase: 'connected',
      connState: 'connected',
      e2eeActive: lk.e2eeActive,
      micEnabled: lk.isMicEnabled(),
      cameraEnabled: lk.isCameraEnabled(),
      screenSharing: lk.isScreenSharing(),
      recordingAvailable: tok.recording_enabled === true,
    });
    if (!lk.e2eeActive) {
      log.warn({ event: 'call.e2ee.unavailable', note: 'media not E2EE on this browser' });
    }
    // Restore the background effect chosen on a previous call. Sets the
    // controller's `desired` even when the camera is currently off, so it
    // re-applies the moment the camera is enabled (audio call → video upgrade).
    const persistedBg = loadBgSelection();
    if (persistedBg && persistedBg.sel.kind !== 'none') {
      set({
        backgroundEffect: persistedBg.sel.kind,
        backgroundImageId: persistedBg.sel.imageId,
      });
      try {
        await setBackground(
          lk.localCameraTrack(),
          persistedBg.sel.kind === 'image' && persistedBg.imageUrl
            ? { kind: persistedBg.sel.kind, imageUrl: persistedBg.imageUrl }
            : { kind: persistedBg.sel.kind },
        );
      } catch (err) {
        log.warn({ event: 'call.background.restore.failed', err: String(err) });
      }
    }
    // Stamp startedAt for duration accounting.
    const cur = useCallStore.getState().active;
    if (cur && cur.startedAt === null) {
      set({ active: { ...cur, startedAt: Date.now() } });
    }
    // No-remote backstop (both directions — calls.md §10). If nobody else is in
    // the room within CALLER_WAIT_MS, give up instead of sitting on the call
    // screen forever. `onParticipants` clears this timer the moment a remote
    // appears, so it only fires when the room stays empty:
    //   • outgoing → 'timeout' (the callee never answered)
    //   • incoming → 'missed'  (we answered but the caller's cancelling `call_end`
    //     was lost, so we joined an already-empty room). Previously the timer was
    //     armed only for outgoing, so an answered-into-empty-room call hung forever.
    clearCallerTimeout();
    if (get().active?.direction !== undefined) {
      callerTimeout = setTimeout(() => {
        callerTimeout = null;
        const s = get();
        if (s.active && s.phase !== 'idle' && !s.participants.some((p) => !p.isLocal)) {
          void s.endCall(s.active.direction === 'outgoing' ? 'timeout' : 'missed');
        }
      }, CALLER_WAIT_MS);
    }
  } catch (err) {
    log.error({ event: 'call.join.failed', err: String(err) });
    // Notify the host conversation so the ring clears AND the call tile flips
    // from "Call ongoing" to "Call failed" — locally echo it too, otherwise THIS
    // device's own invite tile would stay stuck on "Call ongoing" after a failed
    // join (the send is not re-received by the sender). reason 'network_failure'
    // → deriveVariant renders the missed-style "Call failed" tile.
    const cur = get().active;
    if (cur) {
      const endBytes = buildEnd({ call_id: cur.callId, reason: 'network_failure' });
      try {
        const res = await client.getConversation(hexToBytes(cur.conversationId)).send(endBytes);
        const id = await retrieveIdentity();
        if (id) void persistCallEcho(cur.conversationId, endBytes, res, id.accountId);
      } catch {
        /* best-effort */
      }
    }
    // A 404 means the /v1/calls/* routes aren't mounted (LIVEKIT_* not set on
    // the api). An "unknown conversation" / welcome failure means our SDK never
    // processed the per-call group's MLS Welcome (stale key package) — surface a
    // distinct, actionable reason for each instead of silently closing.
    const errStr = String(err);
    const notConfigured = /http 404/i.test(errStr);
    const groupMissing = /unknown conversation|key package|welcome/i.test(errStr);
    toastStore.show(
      notConfigured
        ? 'Calling is unavailable: the server has no LiveKit config (LIVEKIT_* env).'
        : groupMissing
          ? "Couldn't join the call — secure channel not ready. Try again."
          : 'Could not connect to the call.',
      { type: 'error', duration: notConfigured ? 'persistent' : 6000 },
    );
    set({ phase: 'ended', error: notConfigured ? 'Calling unavailable' : 'Could not connect' });
    later(() => reset(set), 1500);
  }
}

/**
 * Derive the media key, retrying while the call group's Welcome is still being
 * processed (callee race). Gives up after ~6s and rethrows.
 */
async function deriveMediaKeyWithRetry(
  client: Parameters<typeof deriveCallMediaKey>[0],
  callGroupId: string,
  callId: string,
): Promise<Uint8Array> {
  const deadline = Date.now() + 6000;
  let lastErr: unknown;
  for (;;) {
    try {
      return await deriveCallMediaKey(client, callGroupId, callId);
    } catch (err) {
      lastErr = err;
      if (Date.now() > deadline) throw lastErr;
      await new Promise((r) => window.setTimeout(r, 250));
    }
  }
}

function dropRing(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  callId: string,
): void {
  const rings = new Map(get().rings);
  rings.delete(callId);
  set({ rings });
}

function pushReaction(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  r: { emoji: string; fromAccount: string; at: number },
): void {
  const id = `${r.at}-${Math.random().toString(36).slice(2, 8)}`;
  const next = [...get().reactions, { id, ...r }].slice(-12);
  set({ reactions: next });
  // Auto-expire after 4s (tracked so teardown cancels it — no post-unmount set).
  later(() => {
    set({ reactions: useCallStore.getState().reactions.filter((x) => x.id !== id) });
  }, 4000);
}

function pushEffect(
  set: (p: Partial<CallStore>) => void,
  get: () => CallStore,
  e: { effect: CallEffectKind; fromName: string; at: number },
): void {
  const id = `${e.at}-${Math.random().toString(36).slice(2, 8)}`;
  // Cap concurrent full-screen effects so a burst can't pile up overlays.
  const next = [...get().effects, { id, ...e }].slice(-4);
  set({ effects: next });
  // The overlay animation runs ~5s; expire slightly after so it fades out fully.
  later(() => {
    set({ effects: useCallStore.getState().effects.filter((x) => x.id !== id) });
  }, 5200);
}
